// What a product's availability actually is, derived rather than stored.
//
// The storefront has shown one hardcoded "Disponible" on every product since the buy block was
// built. Of the 307 active products on prod, 104 are in stock; the other 203 are either sourceable
// at the price shown or sourceable at a price nobody has quoted yet
// (docs/ordering-flow/README.md §2.1).
//
// **Nothing here is a column on the row.** `price` and `available_quantity` are both on
// `SYNC_ALWAYS_WRITES` — the Sunday Alegra run rewrites them every week, unattended. A stored
// `sourcing_mode` would be a second copy of a fact that run already maintains, and it would be
// wrong from the first Sunday a product sold out. Deriving means a product that comes back in stock
// stops saying "bajo pedido" on its own, with nothing to backfill and no way for the two to
// disagree (§3.1, §8.2).
//
// The truth table lives in `availability-cases.json` and is shared with
// `lambda/orders/utils/availability.mjs`, which has to agree with this file about what a shopper
// was promised.

export const AVAILABILITY = {
  /** In the shop right now. */
  IN_STOCK: "IN_STOCK",
  /** Not in stock, but sourceable at the price shown, in `lead_time_days` business days. */
  BACKORDER: "BACKORDER",
  /** Sourceable, but the cost is not known yet — the advisor quotes it over WhatsApp. */
  QUOTE: "QUOTE",
  /** Deactivated. The storefront never renders one; an admin preview can. */
  UNAVAILABLE: "UNAVAILABLE",
} as const;

export type AvailabilityState = (typeof AVAILABILITY)[keyof typeof AVAILABILITY];

/** The shape this module needs. Deliberately structural: full rows, card projections and cart
 *  lines all satisfy it, and so does an order line, which carries no `is_active`. */
export type AvailabilityInput = {
  is_active?: boolean;
  price?: number | string | null;
  available_quantity?: number | string | null;
  lead_time_days?: number | string | null;
};

/**
 * Numbers reach us as numbers, as strings (some DynamoDB paths), and occasionally as free text an
 * admin typed. Anything that is not a finite number reads as `0`, which for both fields means "no"
 * — no price, no stock. Guessing in the other direction would put an unpriced product on sale.
 */
const toNumber = (value: unknown): number => {
  const parsed = typeof value === "number" ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

/**
 * `UNAVAILABLE` only when `is_active` is **explicitly** `false`.
 *
 * An absent `is_active` means the caller is not telling us about activation, not that the product
 * is off — order lines carry no such field, and treating their silence as "deactivated" would make
 * every historical order line read as unavailable. The storefront does not depend on this either
 * way: `ProductDetailPage` and `listItems` both filter `is_active === true` before anything reaches
 * here (EB-503).
 */
export const resolveAvailability = (item: AvailabilityInput): AvailabilityState => {
  if (item.is_active === false) return AVAILABILITY.UNAVAILABLE;
  // Price first: an unknown price outranks stock. A product we cannot put a number on is a
  // quotation regardless of how many are on the shelf.
  if (toNumber(item.price) <= 0) return AVAILABILITY.QUOTE;
  return toNumber(item.available_quantity) > 0 ? AVAILABILITY.IN_STOCK : AVAILABILITY.BACKORDER;
};

/**
 * How many business days this product adds to the promise.
 *
 * `0` for something in stock, `null` for anything whose date is unknowable (quotes, and products
 * that are not on sale). `null` and `0` are meaningfully different and both are real answers, so
 * neither may be collapsed into a falsy check by a caller.
 */
export const resolveLeadTimeDays = (
  item: AvailabilityInput,
  defaultLeadTimeDays: number,
): number | null => {
  const state = resolveAvailability(item);
  if (state === AVAILABILITY.IN_STOCK) return 0;
  if (state !== AVAILABILITY.BACKORDER) return null;

  // An override of `0` is a real answer ("we can get it same-day"), so this cannot be a truthiness
  // check. Anything unparseable or negative falls back rather than promising NaN or a past date.
  const override = item.lead_time_days;
  const parsed = typeof override === "number" ? override : Number(override);
  if (override !== null && override !== undefined && Number.isFinite(parsed) && parsed >= 0) {
    return parsed;
  }
  return defaultLeadTimeDays;
};

/**
 * The whole cart's lead time: the **slowest** line, not the sum and not the first.
 *
 * One in-stock item and one 10-day item is a 10-day order. Splitting the order into two shipments
 * is real operational work this initiative does not fund, so the promise is the one date that is
 * true for the complete delivery (§4.1, §5.4).
 *
 * `QUOTE` lines contribute nothing — their date is unknown by definition, which is why the copy
 * around this number says "para los productos con precio confirmado" whenever the cart holds one.
 * A cart of nothing but quotes returns `null`: there is no date to promise yet.
 */
export const resolveCartLeadTimeDays = (
  items: AvailabilityInput[],
  defaultLeadTimeDays: number,
): number | null => {
  const known = items
    .map((item) => resolveLeadTimeDays(item, defaultLeadTimeDays))
    .filter((days): days is number => days !== null);

  return known.length ? Math.max(...known) : null;
};

/** A line that contributes money to the total. `QUOTE` lines do not; see §3.4. */
export const isPricedLine = (item: AvailabilityInput): boolean =>
  resolveAvailability(item) !== AVAILABILITY.QUOTE;
